import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./components/AuthContext";
import { StoreMetaProvider, useStoreMeta } from "./components/StoreMetaContext";
import Navbar from "./components/Navbar";
import Landing from "./pages/Landing";   // critical-path: anonymous landing
import AdminUsers from "./pages/AdminUsers";   // rendered inside HelpHub tab
import DomiGrillLaunchBanner from "./components/DomiGrillLaunchBanner";
import WageRateBanner from "./components/WageRateBanner";
import ScreenshotGuard from "./components/ScreenshotGuard";
import Footer from "./components/Footer";

// ─── Lazy-loaded routes ─────────────────────────────────────────────────────
// Everything that isn't on the very first paint path is split into its own
// chunk. Initial bundle shrinks from ~1.6 MB → small core; the network
// fetches the page-specific chunk on first navigation. Drastically improves
// cold-start time on flaky / 4G connections.
const Dashboard         = lazy(() => import("./pages/Dashboard"));
const Coach             = lazy(() => import("./pages/Coach"));   // moved off the main bundle (pulls recharts + jspdf + html2canvas)
const NewSubmission     = lazy(() => import("./pages/NewSubmission"));
const Submissions       = lazy(() => import("./pages/Submissions"));
const SubmissionDetail  = lazy(() => import("./pages/SubmissionDetail"));
const Complaints        = lazy(() => import("./pages/Complaints"));
const WeeklyTracker     = lazy(() => import("./pages/WeeklyTracker"));
const SalesBuilding     = lazy(() => import("./pages/SalesBuilding"));
const DataManagement    = lazy(() => import("./pages/DataManagement"));
const NotFound          = lazy(() => import("./pages/NotFound"));
const TermsOfUse        = lazy(() => import("./pages/TermsOfUse"));
const TrainingHub       = lazy(() => import("./pages/TrainingHub"));   // shared Trainee staff-training hub
// Consolidated "room" hubs (tabbed) — each bundles its child pages
const PerformanceHub    = lazy(() => import("./pages/PerformanceHub"));
const HelpHub           = lazy(() => import("./pages/HelpHub"));
const BeyondOperations  = lazy(() => import("./pages/BeyondOperations"));
const DatingTool        = lazy(() => import("./pages/DatingTool"));

function AdminRoute({ children }) {
  // Either admin tier may enter — Area Manager + Super Admin
  const { isAdmin, checking } = useAuth();
  if (checking) return null;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  // Only Super Admin — Data Input paste, Submissions review, password rotation
  const { isSuperAdmin, isAreaManager, storeRole, checking } = useAuth();
  if (checking) return null;
  if (isSuperAdmin) return children;
  // Send Area Manager to KPI (their landing), store to /submit, anon to Landing
  if (isAreaManager) return <Navigate to="/coach" replace />;
  if (storeRole) return <Navigate to="/coach" replace />;
  return <Navigate to="/" replace />;
}

function StoreOnlyRoute({ children }) {
  // For pages that don't make sense for admin tiers (currently none, but available)
  const { storeRole, isAdmin, checking } = useAuth();
  if (checking) return null;
  if (storeRole || isAdmin) return children;
  return <Navigate to="/" replace />;
}

function TrainingRoute({ children }) {
  // Shared "Trainee" staff-training account only.
  const { isTrainee, checking } = useAuth();
  if (checking) return null;
  if (!isTrainee) return <Navigate to="/" replace />;
  return children;
}

/** Suspense fallback rendered while a lazy chunk loads. Subtle to avoid a
 * flash of content while the chunk arrives (usually <300 ms on a warm CDN). */
function RouteLoading() {
  return (
    <div data-testid="route-loading" className="flex items-center justify-center py-24">
      <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider"
           style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
        Loading…
      </div>
    </div>
  );
}

function AppRoutes() {
  const { isSuperAdmin, isAreaManager, storeRole, isTrainee, checking } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const anyRole = isSuperAdmin || isAreaManager || storeRole || isTrainee;

  // When the session is cleared (manual logout OR the 30s poll detecting an
  // expired/revoked session), don't leave the user frozen on a now-unauthorised
  // app page — bounce them back to the login/landing page. Anonymous-safe pages
  // (landing + terms) are exempt so we never loop.
  // Theme scope flag: Liquid Glass only styles the logged-in app (never the landing/login page).
  useEffect(() => {
    if (anyRole) document.body.setAttribute("data-auth", "1");
    else document.body.removeAttribute("data-auth");
  }, [anyRole]);

  useEffect(() => {
    if (checking) return;
    const anonAllowed = location.pathname === "/" || location.pathname === "/terms";
    if (!anyRole && !anonAllowed) navigate("/", { replace: true });
  }, [checking, anyRole, location.pathname, navigate]);

  // Anonymous → Landing.  All logged-in roles now land on /coach (their tailored AI dashboard).
  // Existing pages (/dashboard, /kpi, /submit) remain reachable from the navbar — nothing removed.
  const rootElement = checking
    ? null
    : isTrainee
    ? <Navigate to="/training" replace />
    : isSuperAdmin
    ? <Navigate to="/coach" replace />
    : isAreaManager
    ? <Navigate to="/coach" replace />
    : storeRole
    ? <Navigate to="/coach" replace />
    : <Landing />;

  return (
    <>
      {/* Screenshot deterrent — store-tier only; admins (Sam + Super) are exempt */}
      <ScreenshotGuard enabled={!!storeRole && !isSuperAdmin && !isAreaManager} storeCode={storeRole?.code} />
      <Suspense fallback={<RouteLoading />}>
        <Routes>
          <Route path="/" element={rootElement} />
          <Route path="/coach" element={<Coach />} />
          {/* /activity merged into /sales-building — keep the URL alive as a redirect */}
          <Route path="/activity" element={<Navigate to="/sales-building" replace />} />
          <Route path="/dashboard" element={<SuperAdminRoute><Dashboard /></SuperAdminRoute>} />
          <Route path="/submit" element={<NewSubmission />} />
          <Route path="/submissions" element={<SuperAdminRoute><Submissions /></SuperAdminRoute>} />
          <Route path="/submissions/:id" element={<SuperAdminRoute><SubmissionDetail /></SuperAdminRoute>} />
          <Route path="/kpi" element={<PerformanceHub />} />
          <Route path="/complaints" element={<Complaints />} />
          <Route path="/weekly-tracker" element={<WeeklyTracker />} />
          <Route path="/heatmap" element={<Navigate to="/beyond-operations?tab=domi-heist" replace />} />
          <Route path="/beyond-operations" element={<StoreOnlyRoute><BeyondOperations /></StoreOnlyRoute>} />
          <Route path="/domi-heist" element={<Navigate to="/beyond-operations?tab=domi-heist" replace />} />
          <Route path="/domi-grill" element={<Navigate to="/beyond-operations?tab=domi-grill" replace />} />
          <Route path="/domi-vision" element={<Navigate to="/beyond-operations?tab=domi-vision" replace />} />
          <Route path="/data-management" element={<SuperAdminRoute><DataManagement /></SuperAdminRoute>} />
          <Route path="/data-center" element={<Navigate to="/help?tab=data-center" replace />} />
          <Route path="/sales-building" element={<SalesBuilding />} />
          <Route path="/dating-tool" element={<DatingTool />} />
          <Route path="/help" element={<HelpHub />} />
          <Route path="/customer-service" element={<Navigate to="/complaints?tab=cs-audit" replace />} />
          <Route path="/fixtures" element={<Navigate to="/kpi" replace />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/admin-users" element={<Navigate to="/help?tab=admin-users" replace />} />
          <Route path="/pizza-maker" element={<Navigate to="/training" replace />} />
          <Route path="/training" element={<TrainingRoute><TrainingHub /></TrainingRoute>} />
          <Route path="/quarterly-sales" element={<Navigate to="/kpi?tab=quarterly" replace />} />
          <Route path="/labour" element={<Navigate to="/kpi?tab=labour" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

function Shell() {
  const { isTrainee } = useAuth();
  const { version } = useStoreMeta();
  if (isTrainee) {
    // Trainee gets a distinct, full-bleed training experience (no admin/store chrome).
    return <main className="min-h-screen"><AppRoutes key={version} /></main>;
  }
  return (
    <>
      <Navbar />
      <main className="lg:pl-16 flex flex-col min-h-screen">
        <div className="px-4 md:px-8 max-w-7xl mx-auto pt-4 w-full">
          <DomiGrillLaunchBanner />
          <WageRateBanner />
        </div>
        <div className="flex-1">
          <AppRoutes key={version} />
        </div>
        <Footer />
      </main>
    </>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-white">
      <BrowserRouter>
        <AuthProvider>
          <StoreMetaProvider>
            <Shell />
            <Toaster position="top-right" />
          </StoreMetaProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
